/**
 * Video placements — the named spots on coach pages that show a video, and
 * which video each kind of coach sees there.
 *
 * A placement is registered here only once a page renders it. An entry nobody
 * renders would let an admin set a video no coach ever sees.
 *
 * Each placement holds at most one video per audience. A coach sees the most
 * specific audience that has one: her tier, then her lifecycle, then
 * "everyone". A placement with nothing set for her renders nothing, and the
 * page closes the gap.
 *
 * Pure, importing only the pure coach-account module, so it is safe on either
 * side of the bundle.
 *
 * Aliases: video placements, video slots, intro videos, placement audiences
 */

import {
  COACH_TIER_LABELS,
  type CoachAccount,
} from "~/lib/shared/coach-account";

import type { HelpTopicDefinition } from "./help-topics";

export interface VideoPlacementDefinition {
  /** What admins call the spot. */
  readonly label: string;
  /** Where coaches find it, as an admin would describe it. */
  readonly where: string;
  /**
   * The spot is on a page people see before they sign in. Nobody there has a
   * plan to match, so it holds one video, for Everyone.
   */
  readonly public?: true;
  /**
   * The spot explains one page: its video opens from the page's "How this
   * works" button and from the Help page. It holds one video, for Everyone,
   * and shows only to the topic's role. List topics in menu order: the Help
   * page follows it.
   */
  readonly help?: HelpTopicDefinition;
}

export const VIDEO_PLACEMENTS = {
  welcome: {
    label: "Welcome",
    where: "Welcome, the coach's front door, above the business cards",
  },
  nova_assets: {
    label: "My assets",
    where: "NOVA AI › My assets, beside the Client Acquisition System progress",
  },
  nova_content: {
    label: "My content",
    where: "NOVA AI › My content, beside Why content matters",
  },
  nova_knows_you: {
    label: "What NOVA knows: about you",
    where:
      "NOVA AI › What NOVA knows, first tab under the heading: what to tell NOVA first and how to correct it",
  },
  nova_knows_coaching: {
    label: "What NOVA knows: about coaching businesses",
    where:
      "NOVA AI › What NOVA knows, second tab under the heading: the playbooks, frameworks and profession NOVA works from",
  },
  marketing_suite_welcome: {
    label: "Marketing Suite: welcome",
    where:
      "Marketing Suite sales page, first tab: what the Suite is and what it is not",
  },
  marketing_suite_demo: {
    label: "Marketing Suite: demo",
    where:
      "Marketing Suite sales page, second tab: a walk through the CRM, a funnel, a campaign and booking",
  },
  marketing_suite_story: {
    label: "Marketing Suite: coach story",
    where:
      "Marketing Suite sales page, below the feature cards, in the From a coach block: a coach on how the Suite replaced her other tools. The whole block, caption included, shows only once a video is set",
  },
  nova_home: {
    label: "NOVA Home",
    where:
      "NOVA AI › Home, before the trial once the coach has a score, and as the trial's first step. Without a score, and on paid and churned Home, there is no video",
  },
  plans_and_pricing: {
    label: "Plans and Pricing",
    where:
      "Account › Plans and Pricing, between the notices and the first card: what the plans include and cost. Trial coaches can see a different video from everyone else",
  },
  accelerator_card: {
    label: "Accelerator card",
    where:
      "The Business Builder course page, in the Accelerator card below the weeks: what a week in the Accelerator looks like",
  },
  help_nova_home: {
    label: "Help: NOVA AI home",
    where:
      "NOVA AI › Home, the How this works button beside the title (not on the win-back screen), and the Help page. Coaches only. The NOVA Home spot above is the separate trial intro",
    help: {
      role: "coach",
      section: "my_business",
      description: "Meet NOVA and see where to start.",
    },
  },
  help_client_programs: {
    label: "Help: Client programs",
    where:
      "My clients › Client programs, the How this works button beside the title, and the Help page. Coaches only",
    help: {
      role: "coach",
      section: "my_clients",
      description: "See every program at a glance and start a new one.",
    },
  },
  help_messages: {
    label: "Help: Messages",
    where:
      "My clients › Messages, the Help page. Coaches only: a client on Messages never sees it",
    help: {
      role: "coach",
      section: "my_clients",
      description: "Read and answer your clients in one place.",
    },
  },
  help_invoices: {
    label: "Help: Invoices",
    where:
      "My clients › Invoices, the How this works button beside the title, and the Help page. Coaches only",
    help: {
      role: "coach",
      section: "my_clients",
      description: "Send an invoice and see what your clients have paid.",
    },
  },
  readiness_results: {
    label: "6-Figure Readiness Score results",
    where:
      "Under the score on the public 6-Figure Readiness Score results page, and on the score page in the app for a coach who can start her trial: what the score means and how to get to $100K, with the trial button under it. Coaches past $100K see the strategy call instead",
    public: true,
  },
} as const satisfies Record<string, VideoPlacementDefinition>;

export type VideoPlacementKey = keyof typeof VIDEO_PLACEMENTS;

export const VIDEO_PLACEMENT_KEYS = Object.keys(
  VIDEO_PLACEMENTS,
) as readonly VideoPlacementKey[];

export function isVideoPlacementKey(value: string): value is VideoPlacementKey {
  return Object.hasOwn(VIDEO_PLACEMENTS, value);
}

export type PublicVideoPlacementKey = {
  [K in VideoPlacementKey]: (typeof VIDEO_PLACEMENTS)[K] extends {
    public: true;
  }
    ? K
    : never;
}[VideoPlacementKey];

export function isPublicVideoPlacementKey(
  value: string,
): value is PublicVideoPlacementKey {
  if (!isVideoPlacementKey(value)) return false;
  const definition: VideoPlacementDefinition = VIDEO_PLACEMENTS[value];
  return definition.public === true;
}

/**
 * Who a placement's video is for. `paid` covers every paid tier; `core`, `pro`
 * and `pro_plus` narrow it. Suite only, Graduate and $100K+ are not audiences:
 * the brief still has them moving.
 */
export const VIDEO_AUDIENCES = [
  "everyone",
  "pre_trial",
  "trial",
  "paid",
  "core",
  "pro",
  "pro_plus",
  "churned",
] as const;

export type VideoAudience = (typeof VIDEO_AUDIENCES)[number];

export const VIDEO_AUDIENCE_LABELS: Record<VideoAudience, string> = {
  everyone: "Everyone",
  pre_trial: "Pre-trial",
  trial: "Trial",
  paid: "Paid, any plan",
  ...COACH_TIER_LABELS,
  churned: "Churned",
};

export function isVideoAudience(value: string): value is VideoAudience {
  return (VIDEO_AUDIENCES as readonly string[]).includes(value);
}

/** The audiences an admin can set a video for at `placement`. */
export function placementAudiences(
  placement: VideoPlacementKey,
): readonly VideoAudience[] {
  const definition: VideoPlacementDefinition = VIDEO_PLACEMENTS[placement];
  return definition.public || definition.help ? ["everyone"] : VIDEO_AUDIENCES;
}

/**
 * The audiences a coach matches, most specific first. A paid coach whose tier
 * is not resolved yet (see `CoachAccount.tier`) matches `paid` and `everyone`
 * only — an unknown tier is never read as Core.
 */
export function videoAudienceChain(
  account: Pick<CoachAccount, "lifecycle" | "tier">,
): readonly VideoAudience[] {
  switch (account.lifecycle) {
    case "pre_trial":
    case "trial":
    case "churned":
      return [account.lifecycle, "everyone"];
    case "paid":
      return account.tier
        ? [account.tier, "paid", "everyone"]
        : ["paid", "everyone"];
    default: {
      const exhaustive: never = account.lifecycle;
      throw new Error(`Unknown coach lifecycle: ${String(exhaustive)}`);
    }
  }
}

/**
 * The audience a lookup falls back to when `audience` itself has no video —
 * the next entry in the chain of any coach that `audience` describes. Admin
 * screens use it to say "same as Paid" beside an empty tier.
 */
export const VIDEO_AUDIENCE_FALLBACK: Record<
  VideoAudience,
  VideoAudience | null
> = {
  everyone: null,
  pre_trial: "everyone",
  trial: "everyone",
  paid: "everyone",
  core: "paid",
  pro: "paid",
  pro_plus: "paid",
  churned: "everyone",
};

/** The first audience in `chain` that has a value in `assigned`, if any. */
export function pickForAudience<T>(
  assigned: Partial<Record<VideoAudience, T>>,
  chain: readonly VideoAudience[],
): T | null {
  for (const audience of chain) {
    const value = assigned[audience];
    if (value !== undefined) return value;
  }
  return null;
}

/**
 * The audience whose video an empty `audience` row would show, following the
 * fallback until a set one is found. Null when nothing up the line is set.
 */
export function inheritedAudience(
  assigned: Partial<Record<VideoAudience, unknown>>,
  audience: VideoAudience,
): VideoAudience | null {
  let next = VIDEO_AUDIENCE_FALLBACK[audience];
  while (next !== null) {
    if (assigned[next] !== undefined) return next;
    next = VIDEO_AUDIENCE_FALLBACK[next];
  }
  return null;
}
